import { AppTypeId } from '@ps/platform-message-framework/common/AppTypeId';
import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { ProductId } from '@ps/platform-message-framework/common/ProductId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import produce from 'immer';

import { ConnectionType, connect } from '@swf/core/api/connect';
import { Header } from '@swf/core/enums';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type { RewardsStateModel } from '@swf/core/types/RewardsLegacy';

interface RewardsModel extends ModelConfig<RewardsState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

interface ExchangeResponse {
  id: number;
  type: string;
  success: boolean;
  error?: string;
}

interface ExchangeParams {
  response: ExchangeResponse | null;
}

export interface RewardsState {
  current?: RewardsStateModel;
  queue: RewardsStateModel[];
  optedIn?: boolean;
  exchange: ExchangeParams;
}

const ANIMATION_TIMEOUT = 1650;
let animationQueueTimer: number;

const defaultState = {
  queue: [],
  exchange: { response: null }
};

export enum RewardsBrand {
  FoxBet = 'FOX Bet',
  PokerStars = 'PokerStars'
}

const reducers = {
  add: (state: RewardsState, payload: RewardsStateModel): RewardsState =>
    produce(state, (draft) => {
      draft.queue.push(payload);
    }),
  remove: (state: RewardsState): RewardsState =>
    produce(state, (draft) => {
      draft.current = draft.queue.shift();
    }),
  'session/clear': (): RewardsState => defaultState,
  setOptIn: (state: RewardsState, payload: boolean): RewardsState => ({
    ...state,
    optedIn: payload
  }),
  setExchangeStatus: (state: RewardsState, payload: ExchangeParams): RewardsState =>
    produce(state, (draft) => {
      draft.exchange = payload;
    })
};

const effects = {
  async processItem(payload: undefined, { rootState, dispatch }: EffectParams) {
    const {
      rewardsLegacy: { queue }
    } = rootState;

    if (queue.length) {
      dispatch.rewardsLegacy.remove();
      dispatch.rewardsLegacy.processQueue();
    } else {
      clearTimeout(animationQueueTimer);
      animationQueueTimer = 0;
    }
  },
  async processQueue(payload: undefined, { dispatch }: EffectParams) {
    // eslint-disable-next-line stars-web/no-direct-browser-globals-access
    animationQueueTimer = window.setTimeout(() => {
      dispatch.rewardsLegacy.processItem();
    }, ANIMATION_TIMEOUT);
  },
  async enqueue(payload: RewardsStateModel, { dispatch }: EffectParams) {
    dispatch.rewardsLegacy.add(payload);
    if (!animationQueueTimer) {
      dispatch.rewardsLegacy.processQueue();
    }
  },
  async optIn(payload: undefined, { dispatch, request }: EffectParams) {
    try {
      await request({
        service: '/api/v1-preview/account/cvl/optIn',
        method: 'POST',
        http: true
      });
      dispatch.rewardsLegacy.setOptIn(true);
    } catch {
      // TODO: display error message
      dispatch.rewardsLegacy.setOptIn(false);
    }
  },
  async exchange(payload: undefined, { dispatch, rootState, request, select }: EffectParams) {
    const isExternalPartnerSite = select.cms.isExternalPartnerSite(rootState);
    if (isExternalPartnerSite) {
      const headersKong = select.rewardsLegacy.getKongHeaders(rootState);
      const { body } = await request({
        headers: headersKong,
        service: '/app/starsrewards/api',
        body: { id: 1, type: 'exchange' },
        connection: connect(ConnectionType.RewardsKong)
      });
      dispatch.rewardsLegacy.setExchangeStatus({ response: body });
    } else {
      const options = select.cms.getRequestOptions(rootState);
      const { body } = await request({
        ...options,
        service: 'starsrewards',
        body: { id: 1, type: 'exchange' },
        connection: connect(ConnectionType.Rewards)
      });
      dispatch.rewardsLegacy.setExchangeStatus({ response: body });
    }
  }
};

const selectors = (slice: Slicer<RewardsState>, createSelector: SelectorCreator) => ({
  get() {
    return slice((state) => (state.current || {}) as Partial<RewardsStateModel>);
  },
  getChestProps() {
    return slice((state) =>
      Object.assign(
        {
          tierId: 0,
          currentPoints: 0,
          targetPoints: 0,
          expanded: false,
          cvlConfig: { cvl2: false }
        },
        state.current
      )
    );
  },
  getExchangeStatus() {
    return slice((state) => state.exchange);
  },
  getRewardsBrand() {
    return createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (brand) => (brand === BrandTypes.FOX_BET ? RewardsBrand.FoxBet : RewardsBrand.PokerStars)
    );
  },
  getKongHeaders() {
    return createSelector(
      (rootState: RootState) => select.session.get(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.device.getId(rootState),
      (rootState: RootState) => select.device.getOS(rootState),
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (rootState: RootState) => select.device.getPlatform(rootState),
      ({ installId, idpToken }, site, device, os, brand, platform) => ({
        authorization: `Bearer ${idpToken}`,
        [Header.FiInstallId]: `${installId}`,
        [Header.FiSiteId]: `${site}`,
        [Header.FiDeviceId]: `${device}`,
        [Header.FiOsId]: `${os}`,
        [Header.FiBrandId]: `${brand}`,
        [Header.FiPlatformId]: `${platform}`,
        [Header.FiAppTypeId]: `${AppTypeId.CHEST_WIDGET}`,
        [Header.FiProductId]: `${ProductId.POKER}`
      })
    );
  }
});

const rewardsLegacy: RewardsModel = {
  state: defaultState,
  reducers,
  effects,
  selectors
};

export default rewardsLegacy;
